import { Component, computed, effect, inject, resource, signal, untracked } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FormSchemaDataDto } from '@unity/clients';
import { FormSchemaService } from '@unity/services/form-schema.service';
import { SurveyCreatorModule } from 'survey-creator-angular';
import type { SurveyCreatorModel } from 'survey-creator-core';

@Component({
  selector: 'unity-survey-creator-accessor',
  standalone: true,
  imports: [SurveyCreatorModule],
  templateUrl: './survey-creator-accessor.component.html',
  styleUrl: './survey-creator-accessor.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SurveyCreatorAccessorComponent,
    },
  ],
})
export class SurveyCreatorAccessorComponent implements ControlValueAccessor {
  private readonly formSchemaService = inject(FormSchemaService);

  private readonly surveyCreatorModelResource = resource({
    loader: () => this.formSchemaService.surveyCreatorModel(),
  });

  private readonly $hostValue = signal<FormSchemaDataDto | null>(null);

  protected readonly $surveyCreatorModel = computed(() => this.surveyCreatorModelResource.value());

  public constructor() {
    // Initial set- reactive in case of model load delay
    effect(() => {
      const model = this.$surveyCreatorModel();
      if (model) {
        model.onModified.add(m => this.onValueChanged(m));

        const value = untracked(this.$hostValue);
        model.JSON = value ?? {};
      }
    });
  }

  // Write back to formControl
  private onChange: ((value: FormSchemaDataDto) => void) | undefined;
  private onTouched: (() => void) | undefined;

  private onValueChanged(model: SurveyCreatorModel) {
    if (this.onChange) {
      this.onChange(model.JSON);
    }
    if (this.onTouched) {
      this.onTouched();
      model.isTouch = true;
    }
  }

  // Used for initial set (write from parent)
  public writeValue(value?: FormSchemaDataDto): void {
    if (value) {
      this.$hostValue.set(value);
    }
  }

  public registerOnChange(fn: (value?: FormSchemaDataDto) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
